/* eslint-disable no-unused-expressions */
import axios from 'axios';
import Cookies from 'universal-cookie';

import { accountTypeCommercial, cobrowseDomain, cobrowseURL, messages, piService } from './constants';

import { generatePINXTQueryParams, base64Encode } from './pinxtHelpers';

//TODO: create an axios Custom instance default to handle agentJWT in search request
const cookie = new Cookies();
let host;

const DOMAIN_ENUM = {
  RESI: 'spectrum.net',
  SMB: 'spectrumbusiness.net',
};

/**
 * Decodes id token returned from fetchToken request.
 *
 * @param id_token - agentJwt token
 * @return {{access: {account: boolean, agent: boolean}}}
 */
export const decodeToken = id_token => {
  let access = {};
  let base64Url = id_token.split('.')[1];
  let base64 = base64Url.replace(/-/g, '+').replace(/-/g, '/');
  let jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  const { routes, Email } = JSON.parse(jsonPayload);
  access.email = Email;
  routes.includes('/v1/audit/account') ? (access.account = true) : null;
  routes.includes('/v1/audit/agent') ? (access.agent = true) : null;

  return access;
};

/**
 * Returns environment-specific configuration based on current origin.
 */
export const returnHostUrl = () => {
  const originName = window.location.origin;

  // Map each environment to its config
  const envConfigs = [cobrowseURL.local, cobrowseURL.dev, cobrowseURL.uat, cobrowseURL.stage, cobrowseURL.prod];

  // Find the config that matches the current origin
  let matchedConfig = envConfigs.find(config => config.ui === originName || config.smbUi === originName);

  // Fallback to dev if not found
  if (!matchedConfig) matchedConfig = cobrowseURL.dev;

  if (originName === matchedConfig.ui) {
    host = matchedConfig.host;
  } else if (originName === matchedConfig.smbUi) {
    host = matchedConfig.commercialHost;
  } else {
    // fallback, match dev
    host = originName === cobrowseURL.dev.ui ? cobrowseURL.dev.host : cobrowseURL.dev.commercialHost;
  }

  // Determine if this is a Resi or SMB domain
  const isResi = !originName.includes(DOMAIN_ENUM.SMB);

  // Build the domain info object
  const domainInfo = {
    host: matchedConfig.host,
    cobrowseResiUI: matchedConfig.ui,
    cobrowseSMBUI: matchedConfig.smbUi,
    loginUrl: matchedConfig.loginUrl,
    smbLoginUrl: matchedConfig.smbLoginUrl,
    commercialHost: matchedConfig.commercialHost,
    spectrumPortalsHost: matchedConfig.specPortals,
    smbPortalsHost: matchedConfig.smbPortals,
    isResi,
  };

  return domainInfo;
};

/**
 * Retrieves authentication code value from Url bar.
 *
 * @return {{authCode: *string, divisionBillingId: *string, systemError: *string, accountNumber: *string}}
 */
export const getAuthCode = () => {
  let authCode;
  let accountNumber;
  let divisionBillingId;
  let systemError;

  const url = new URL(window.location.href);
  const urlParams = url.searchParams;

  authCode = urlParams.get(cobrowseDomain.authCodeParam);
  url.searchParams.delete(cobrowseDomain.authCodeParam);

  if (urlParams.has(cobrowseDomain.accountIdParam)) {
    accountNumber = urlParams.get(cobrowseDomain.accountIdParam);
    url.searchParams.delete(cobrowseDomain.accountIdParam);
  }

  if (urlParams.has(cobrowseDomain.accountNumberParam)) {
    accountNumber = urlParams.get(cobrowseDomain.accountNumberParam);
    url.searchParams.delete(cobrowseDomain.accountNumberParam);
  }

  if (urlParams.has(cobrowseDomain.divisionIdParam)) {
    divisionBillingId = urlParams.get(cobrowseDomain.divisionIdParam);
    url.searchParams.delete(cobrowseDomain.divisionIdParam);
  }

  if (urlParams.has(cobrowseDomain.systemErrorParam)) {
    systemError = urlParams.get(cobrowseDomain.systemErrorParam);
    url.searchParams.delete(cobrowseDomain.systemErrorParam);
  }

  window.history.replaceState({ remove: cobrowseDomain.authCodeParam }, '', url.toString());

  return {
    authCode,
    accountNumber,
    divisionBillingId,
    systemError,
  };
};

/**
 * Returns to(today) & from(prev 7 days) dates.
 *
 * @return {{fromDate: Date[string], toDate: Date[string]}}
 */
export const getDates = () => {
  const now = Date.now();
  const today = new Date(now);
  const month = ('0' + (today.getMonth() + 1)).slice(-2);
  const date = ('0' + today.getDate()).slice(-2);

  const fromDateDefault = now - 24 * 60 * 60 * 1000 * 7;
  const fDate = new Date(fromDateDefault);
  const fromDateMonth = ('0' + (fDate.getMonth() + 1)).slice(-2);
  const fromDateDate = ('0' + fDate.getDate()).slice(-2);

  const toDate = `${today.getFullYear()}-${month}-${date}`;
  const fromDate = `${fDate.getFullYear()}-${fromDateMonth}-${fromDateDate}`;

  return {
    toDate,
    fromDate,
  };
};

/**
 * Performs axios get request to the Pi Auth service with authentication code.
 *
 * @param {string} authCode
 * @return {Promise<AxiosResponse<T>>}
 */
export const fetchToken = authCode => {
  const params = new URLSearchParams();
  params.append('client_id', cobrowseDomain.cobrowseService);
  params.append('grant_type', cobrowseDomain.authorizationCode);
  params.append('code', authCode);

  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded',
    'Cache-Control': 'no-cache',
  };

  let url = `${host}/${piService.piAuthService}`;

  return axios.post(url.toString(), params, { headers });
};

/**
 * Performs axios post request to the CoBrowse domain service account search API.
 *
 * @param {object} criteria - search criteria
 * @return {Promise<AxiosResponse<T>>}
 */
export const fetchAccount = criteria => {
  const accessToken = sessionStorage.getItem(piService.accessToken);
  const agentToken = sessionStorage.getItem(piService.agentToken);
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${accessToken}`,
    AgentJWT: `${agentToken}`,
    AccessToken: `${accessToken}`,
    'Content-Type': 'application/json',
  };

  let url = `${host}/${cobrowseDomain.cobrowseServiceAPI}/v1/search/account`;

  return axios.post(url, criteria, { headers });
};

export const fetchSwitchTypeData = criteria => {
  const accessToken = sessionStorage.getItem(piService.accessToken);
  const agentToken = sessionStorage.getItem(piService.agentToken);
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${accessToken}`,
    AgentJWT: `${agentToken}`,
    AccessToken: `${accessToken}`,
    'Content-Type': 'application/json',
  };

  let url = `${host}/${cobrowseDomain.cobrowseServiceAPI}/v1/search/switch_type`;

  return axios.get(url, { ...criteria, headers });
};

/**
 * Performs axios get request to the CoBrowse domain service account history API.
 *
 * @param criteria - search criteria
 * @return {Promise<AxiosResponse<T>>}
 */
export const fetchAccountReport = criteria => {
  const accessToken = sessionStorage.getItem(piService.accessToken);
  const agentToken = sessionStorage.getItem(piService.agentToken);
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${accessToken}`,
    AgentJWT: `${agentToken}`,
    AccessToken: `${accessToken}`,
    'Content-Type': 'application/json',
  };

  criteria = {
    FromDate: criteria.fromDate,
    ToDate: criteria.toDate,
    AccountNumber: criteria.accountNumber,
    DivisionBillingId: criteria.divisionId,
    CustomerType: criteria.accountType,
  };

  let url = `${host}/${cobrowseDomain.cobrowseServiceAPI}/v1/audit/account`;

  return axios.post(url.toString(), criteria, { headers });
};

/**
 * Performs axios post request to the CoBrowse domain service account report download API.
 *
 * @param criteria - search criteria
 * @return {Promise<AxiosResponse<T>>}
 */
export const exportAccountReportToExcel = criteria => {
  const accessToken = sessionStorage.getItem(piService.accessToken);
  const agentToken = sessionStorage.getItem(piService.agentToken);
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    AgentJWT: `${agentToken}`,
    AccessToken: `${accessToken}`,
    'Content-Type': 'application/json',
  };

  criteria = {
    FromDate: criteria.fromDate,
    ToDate: criteria.toDate,
    AccountNumber: criteria.accountNumber,
    DivisionBillingId: criteria.divisionId,
    CustomerType: criteria.accountType,
  };

  let url = `${host}/${cobrowseDomain.cobrowseServiceAPI}/v1/audit/account/download`;

  return axios.post(url.toString(), criteria, {
    headers,
    responseType: 'blob',
  });
};

/**
 * Performs axios get request to the CoBrowse domain service agent search API.
 *
 * @param criteria - search criteria
 * @return {Promise<AxiosResponse<T>>}
 */
export const fetchCSRAgent = criteria => {
  const accessToken = sessionStorage.getItem(piService.accessToken);
  const agentToken = sessionStorage.getItem(piService.agentToken);
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${accessToken}`,
    AgentJWT: `${agentToken}`,
    AccessToken: `${accessToken}`,
    'Cache-Control': 'no-cache',
    'Content-Type': 'application/json',
  };

  let url = `${host}/${cobrowseDomain.cobrowseServiceAPI}/v1/search/agent`;

  return axios.post(url, criteria, { headers });
};

/**
 * Performs axios get request to the CoBrowse domain service agent report API.
 *
 * @param criteria - search criteria
 * @return {Promise<AxiosResponse<T>>}
 */
export const fetchCSRAgentReport = criteria => {
  const accessToken = sessionStorage.getItem(piService.accessToken);
  const agentToken = sessionStorage.getItem(piService.agentToken);
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${accessToken}`,
    AgentJWT: `${agentToken}`,
    AccessToken: `${accessToken}`,
    'Content-Type': 'application/json',
  };

  criteria = {
    FromDate: criteria.fromDate,
    ToDate: criteria.toDate,
    AgentId: criteria.agentId,
  };

  let url = `${host}/${cobrowseDomain.cobrowseServiceAPI}/v1/audit/agent`;

  return axios.post(url.toString(), criteria, { headers });
};

/**
 * Performs axios post request to the CoBrowse domain service agent report download API.
 *
 * @param criteria - search criteria
 * @return {Promise<AxiosResponse<T>>}
 */
export const exportAgentReportToExcel = criteria => {
  const accessToken = sessionStorage.getItem(piService.accessToken);
  const agentToken = sessionStorage.getItem(piService.agentToken);
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    AgentJWT: `${agentToken}`,
    AccessToken: `${accessToken}`,
    'Content-Type': 'application/json',
  };

  criteria = {
    FromDate: criteria.fromDate,
    ToDate: criteria.toDate,
    AgentId: criteria.agentId,
  };

  let url = `${host}/${cobrowseDomain.cobrowseServiceAPI}/v1/audit/agent/download`;

  return axios.post(url.toString(), criteria, {
    headers,
    responseType: 'blob',
  });
};

/**
 * Performs axios post request to the CoBrowse domain service initialize user API.
 *
 * @param criteria - search criteria
 * @return {Promise<AxiosResponse<T>>}
 */
export const initializeVOM = async criteria => {
  const accessToken = sessionStorage.getItem(piService.accessToken);

  const params = await generatePINXTQueryParams('consumer_portal');

  const host = returnHostUrl().host;

  // Account GUID Authentication
  if (criteria.accountGuid) {
    const accountGuidAccessToken = base64Encode(`${criteria.accountGuid}:${accessToken}`);
    const headers = {
      Accept: 'application/json',
      Authorization: `Basic ${accountGuidAccessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded',
      'cache-control': 'no-cache',
    };

    return axios.post(`${host}/auth/oauth/v2/cobrowse/user/agent-token/auth`, params, {
      headers,
      withCredentials: true,
    });
  }

  // Identity GUID Authentication
  const identityGuidAccessToken = base64Encode(`${criteria.identityGuid}:${accessToken}`);

  const headers = {
    Accept: 'application/json',
    Authorization: `Basic ${identityGuidAccessToken}`,
    'Content-Type': 'application/x-www-form-urlencoded',
    'cache-control': 'no-cache',
  };

  return axios.post(`${host}/auth/oauth/v2/cobrowse/user/agent-token/auth`, params, { headers, withCredentials: true });
};

/**
 * Performs axios post request to the CoBrowse domain service initialize user API.
 *
 * @param criteria - search criteria
 * @return {Promise<AxiosResponse<T>>}
 */
export const initializeVAS = async criteria => {
  const accessToken = sessionStorage.getItem(piService.accessToken);

  const params = await generatePINXTQueryParams('commercial_portal');

  const host = returnHostUrl().commercialHost;

  // Account GUID Authentication
  if (criteria.accountGuid) {
    const accountGuidAccessToken = base64Encode(`${criteria.accountGuid}:${accessToken}`);
    const headers = {
      Accept: 'application/json',
      Authorization: `Basic ${accountGuidAccessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded',
      'cache-control': 'no-cache',
    };

    return axios.post(`${host}/auth/oauth/v2/cobrowse/user/agent-token/auth`, params, {
      headers,
      withCredentials: true,
    });
  }

  // Identity GUID Authentication
  const identityGuidAccessToken = base64Encode(`${criteria.identityGuid}:${accessToken}`);

  const headers = {
    Accept: 'application/json',
    Authorization: `Basic ${identityGuidAccessToken}`,
    'Content-Type': 'application/x-www-form-urlencoded',
    'cache-control': 'no-cache',
  };

  return axios.post(`${host}/auth/oauth/v2/cobrowse/user/agent-token/auth`, params, { headers, withCredentials: true });
};

export const initializeVoiceMFE = async (criteria, isSMB) => {
  const accessToken = sessionStorage.getItem(piService.accessToken);

  const params = await generatePINXTQueryParams(isSMB ? 'commercial_portal' : 'consumer_portal');

  const host = isSMB ? returnHostUrl().commercialHost : returnHostUrl().host;

  // Account GUID Authentication
  if (criteria.accountGuid) {
    const accountGuidAccessToken = base64Encode(`${criteria.accountGuid}:${accessToken}`);
    const headers = {
      Accept: 'application/json',
      Authorization: `Basic ${accountGuidAccessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded',
      'cache-control': 'no-cache',
    };

    return axios.post(`${host}/auth/oauth/v2/cobrowse/user/agent-token/auth`, params, {
      headers,
      withCredentials: true,
    });
  }

  // Identity GUID Authentication
  const identityGuidAccessToken = base64Encode(`${criteria.identityGuid}:${accessToken}`);

  const headers = {
    Accept: 'application/json',
    Authorization: `Basic ${identityGuidAccessToken}`,
    'Content-Type': 'application/x-www-form-urlencoded',
    'cache-control': 'no-cache',
  };

  return axios.post(`${host}/auth/oauth/v2/cobrowse/user/agent-token/auth`, params, { headers, withCredentials: true });
};

export const initializeVZVM = criteria => {
  const accessToken = sessionStorage.getItem(piService.accessToken);
  const agentToken = sessionStorage.getItem(piService.agentToken);
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${accessToken}`,
    AgentJWT: `${agentToken}`,
    AccessToken: `${accessToken}`,
    'Content-Type': 'application/json',
  };

  let url;

  if (criteria.accountType === accountTypeCommercial) {
    url = `${returnHostUrl().commercialHost}/${cobrowseDomain.cobrowseServiceAPI}/v1/initialize/user`;
  } else {
    url = `${host}/${cobrowseDomain.cobrowseServiceAPI}/v1/initialize/user`;
  }

  return axios.post(url.toString(), criteria, {
    headers,
    withCredentials: true,
  });
};

axios.interceptors.response.use(
  function(res) {
    return res;
  },
  function(err) {
    let error = {};
    if (!err.response) {
      error = {
        ...err,
        message: 'Network Error',
      };
      return Promise.reject(error);
    }
    if (401 === err.response.status) {
      // resultCode comes from PI
      if (err.response.data.resultCode) {
        cookie.remove(piService.agentToken);
        cookie.remove(piService.accessToken);
        let url = returnHostUrl().loginUrl;
        window.location.replace(url);
      } else {
        error = {
          ...err,
          message: messages.invalidTokenMessage,
          response: {
            ...err.response,
            data: {
              ...err.response.data,
              message: messages.invalidTokenMessage,
            },
          },
        };
        return Promise.reject(error);
      }
    }
    if (403 === err.response.status) {
      error = {
        ...err,
        message: messages.noAccessMessage,
        response: {
          ...err.response,
          data: {
            ...err.response.data,
            message: messages.noAccessMessage,
          },
        },
      };
      return Promise.reject(error);
    }
    if (500 === err.response.status) {
      error = {
        ...err,
        message: messages.unexpectedErrorMessage,
        response: {
          ...err.response,
          data: {
            ...err.response.data,
            message: messages.unexpectedErrorMessage,
          },
        },
      };
      return Promise.reject(error);
    }

    return Promise.reject(err);
  }
);
